import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MaskPipe } from 'ngx-mask';
import { Subscription } from 'rxjs/Subscription';
import { ContactTypeValue } from '../../../common/enums/contact-type.value.enum';
import { TranslateKey } from '../../../common/pipes/translate-key.pipe';
import { CommonService } from '../../../shared/services/common.service';
import { TitleValue } from '../../common/enumerations';
import { AddressSchemaFormFrameworkType } from '../../enums/address-type.enum';
import { PersonalInformation } from '../../interface/enrollment';
import { JsonSchemaFormOptions } from '../../interface/enrollment/personal-info.interface';
import { PersonalInfoConfig } from '../../interface/personal-info-config.interface';
import { AddressService } from '../../services/address.service';
import { ConfigurationService } from '../../services/configuration.service';
import { PersonalInfoConfigService } from '../../services/personal-info-config.service';
import { PersonalMessageService } from '../../services/personal-schema-form.service';
import { CustomDatePickerComponent } from '../../widgets/custom-datepicker.widget';
import { CustomMaskTextboxComponent } from '../../widgets/custom-mask-textbox.widget';
import { CustomSelectWidgetComponent } from '../../widgets/custom-select.widget';
import { CustomInputComponent } from '../../widgets/custom-textbox.widget';


/**
 * @description this component help to
 * validate the personal info
 * @date 2018-08-15
 * @export
 * @class PersonalInfoFormComponent
 * @implements {OnInit}
 */
@Component({
    selector: 'app-personal-info-form',
    templateUrl: '../../templates/template1/views/enrollment/personal-schema-info.component.html',
    styleUrls: ['../../templates/template1/themes/default/css/personal-info-form.component.less']
})
export class PersonalInfoFormComponent implements OnInit, OnDestroy {


    @Input() personalInfoSchemaUrl: string;
    @Input() preloadPersonalInformation: PersonalInformation;
    @Output() personalInfoSubmit = new EventEmitter<any>();
    @Output() personalInfoSubmitOnInputChange = new EventEmitter<any>();
    personalInfoConfig: PersonalInfoConfig;
    jsonFormObject: any;
    displayPersonalInfoForm = false;
    selectedFrameWork = AddressSchemaFormFrameworkType.BOOTSTRAP4;
    jsonFormOptions: JsonSchemaFormOptions = {
        addSubmit: true, // Add a submit button if layout does not have one
        loadExternalAssets: false, // Load external css and JavaScript for frameworks
        debug: false,
        setSchemaDefaults: true,
        returnEmptyFields: false,
    };
    liveFormData: PersonalInformation;
    personalInfoFormValidated: boolean;
    customWidgets = {
        'custom-select-widget': CustomSelectWidgetComponent,
        'custom-input-widget': CustomInputComponent,
        'custom-mask-widget': CustomMaskTextboxComponent,
        'custom-datepicker-widget': CustomDatePickerComponent
    };
    editPersonalSubscription: Subscription;
    interval: NodeJS.Timer;
    personalInformation: PersonalInformation;
    personalInfoFormSubscription: Subscription;
    validationMessageInitialHide = true;
    enrollCountryCode: string;

    constructor(
        private _addressService: AddressService,
        private _elementRef: ElementRef,
        private _renderer: Renderer,
        private _chRef: ChangeDetectorRef,
        private _translatePipe: TranslateService,
        private _translateKey: TranslateKey,
        private _maskPipe: MaskPipe,
        private _commonService: CommonService,
        private _personalInfoConfigService: PersonalInfoConfigService,
        private _configurationService: ConfigurationService,
        private _personalMessageService: PersonalMessageService
    ) { }

    ngOnInit() {
        this.loadDefaultSettings();
    }

    /**
    * @description it used to load default configuration
    * of the current component based on user
    * @date 2018-07-19
    * @private
    * @memberof PersonalInfoComponent
    */
    private loadDefaultSettings(): void {
        this.enrollCountryCode = this._configurationService.getEnrollCountry();
        this.personalInfoConfig = this._personalInfoConfigService.getSettings(this.enrollCountryCode);
        this.getPersonalInfoJsonSchema();
        this.personalInfoFormSubject();
        this.rebindPersonalInfoFormSubject();
    }

    /**
    * @description this method used
    * subscribe changes of form submit
    * event
    * @date 2018-07-24
    * @private
    * @memberof PersonalInfoComponent
    */
    private personalInfoFormSubject(): void {
        this.personalInfoFormSubscription = this
            ._personalMessageService.getSubmitPersonalInfoForm()
            .subscribe(isSubmit => {
                this.submitPersonalInfoForm();
            });
        }

    /**
    * @description this method used
    * subscribe changes of form submit
    * event
    * @date 2018-07-24
    * @private
    * @memberof PersonalInfoComponent
    */
   private rebindPersonalInfoFormSubject(): void {
    this.personalInfoFormSubscription = this
        ._personalMessageService.getRebindPersonalInfoForm()
        .subscribe(isSubmit => {
            this.enrollCountryCode = this._configurationService.getEnrollCountry();
            this.personalInfoConfig = this._personalInfoConfigService.getSettings(this.enrollCountryCode);
            this.personalInfoSchemaUrl = this._configurationService.getPersonalInfoSchemaURL();
            this.getPersonalInfoJsonSchema();
        });
    }

    /**
     * @description this method will subscribe changes
     * when we need to submit guest info
     * @date 2018-07-24
     * @memberof GuestPersonalInfoComponent
     */
    submitPersonalInfoForm(): void {
        // submiting personal information
        if (!this.personalInfoFormValidated) {
            const element = this._elementRef.nativeElement.getElementsByClassName('personal-btn');
            if (element && element[0]) {
                element[0].click();
            }
        }
    }

    /**
     * to get the country specific schema json file
     * @returns void
     */
    getPersonalInfoJsonSchema(): void {
        this._addressService.getJsonFormSchema(this.personalInfoSchemaUrl)
            .subscribe((response: any) => {
                //get parse json schema data
                this.jsonFormObject = this.getParsedSchemaFormJson(response);
                this.prePopulateUserInfoFields(this.jsonFormObject);
                this.findErrorElement();
            });
    }

    /**
     * get parsed json form object
     * @param  {any} jsonText
     * @returns any
     */
    getParsedSchemaFormJson(jsonText: any): any {
        let newFormObject: any = null;
        try {
            // Parse entered content as JSON
            newFormObject = JSON.parse(jsonText);
        } catch (jsonError) {
            try {
                // If entered content is not valid JSON,
                // parse as JavaScript instead to include functions
                newFormObject = null;

            } catch (javascriptError) {
                return;
            }
        }
        return newFormObject;
    }

    /**
     * @returns void
     */
    findErrorElement(): void {
        this.interval = setInterval(() => {
            if (document.getElementsByClassName('help-block').length > 0) {
                this.hideErrorElement(true);
            }
        }, 100);
        setTimeout(() => {
            this.validationMessageInitialHide = false;
        }, 1000);
    }

    /**
     * @param  {boolean} hide
     * @returns void
     */
    private hideErrorElement(hide: boolean): void {
        const elements = this._elementRef.nativeElement.getElementsByClassName('help-block');
        if (elements) {
            for (let i = 0; i < elements.length; i++) {
                if (hide) {
                    this._renderer.setElementClass(elements[i], 'hide', true);
                    this._renderer.setElementClass(elements[i], 'show', false);
                } else {
                    this._renderer.setElementClass(elements[i], 'show', true);
                    this._renderer.setElementClass(elements[i], 'hide', false);
                }
            }
            clearInterval(this.interval);
        }
    }


    /**
     * @description this method will help to
     * load data while changing the inputvalue
     * @date 2018-08-15
     * @param {PersonalInformation} data
     * @memberof PersonalInfoFormComponent
     */
    onChanges(data: PersonalInformation) {
        this.liveFormData = data;
    }

    /**
     * @description it will trigger when we
     * change any input value in form
     * @date 2018-08-02
     * @memberof AddressComponent
     */
    changeInputValue(event: any): void {
        this.personalInfoFormValidated = false;
        this.personalInfoSubmitOnInputChange.emit(this.personalInfoFormValidated);
    }


    /**
     * @description this method will be used to perpopulate
     * user details in schma form
     * @date 2018-08-15
     * @param {*} jsonSchemaFormObject
     * @memberof PersonalInfoFormComponent
     */
    //just to populate mr and some default
    prePopulateUserInfoFields(jsonSchemaFormObject: any): void {
        this.jsonFormObject = jsonSchemaFormObject;
        if (this.preloadPersonalInformation) {
            const phoneNumberFormat= this._commonService.getPhoneNumberFormatByCountryCode(this.enrollCountryCode);
            if (this.preloadPersonalInformation.phoneNumber) {
                if(phoneNumberFormat){
                    this.preloadPersonalInformation.phoneNumber = this._maskPipe
                    .transform(this.preloadPersonalInformation.phoneNumber,phoneNumberFormat);
                }else{
                    this.preloadPersonalInformation.phoneNumber = this.preloadPersonalInformation.phoneNumber;
                }
            }
            this.jsonFormObject.data = this.preloadPersonalInformation;
        } else {
          if (this.jsonFormObject) {
            this.jsonFormObject.data.languagepreference = this._configurationService.getLanguageCulture();
            this.jsonFormObject.data.phoneType = ContactTypeValue.MOBILE;
            this.jsonFormObject.data.title = TitleValue.MR;
            this.jsonFormObject.data.newsLetterLanguage = this._configurationService.getLanguageCulture();
          }

        }
        //to show our data in UI
        this.getTranslatedSchemaData(this.jsonFormObject);
    }

    /**
      * to save address component
      * @param  {any} address
      * @returns void
      */
    savePersonalInfo(personalInfo: PersonalInformation): void {
        const customErrors = this.getCustomErrors(personalInfo);
        if (personalInfo) {
            if ((!customErrors || (customErrors
                && !customErrors.find(x => x === 'InProgress')
                && (customErrors.length === 0 || customErrors[0] === '')))) {
                setTimeout(() => {
                    if (!customErrors || (customErrors
                        && !customErrors.find(x => x === 'InProgress')
                        && (customErrors.length === 0 || customErrors[0] === ''))) {
                        this.submitPersonalInfo(personalInfo, true);
                    } else if (this.preloadPersonalInformation
                        && this.preloadPersonalInformation.isAutoPopulate) {
                        this.submitPersonalInfo(personalInfo, true);
                    } else {
                        this.submitPersonalInfo(personalInfo, false);
                    }
                }, 2000);
            } else {
                this.submitPersonalInfo(personalInfo, false);
                this.hideErrorElement(false);
            }
        } else {
            this.submitPersonalInfo(personalInfo, false);
            this.hideErrorElement(false);
        }
    }

    /**
     * @description it used to find the custom errors
     * based on unique validations
     * @date 2018-08-15
     * @returns {any[]}
     * @memberof PersonalInfoFormComponent
     */
    getCustomErrors(personalInfo: PersonalInformation): any[] {
        let customErrors = [];
        const length: number = sessionStorage.length;
        for (let i = 0; i < length; i++) {
            const key = sessionStorage.key(i);
            if (!(sessionStorage[key] === 'true') && key.indexOf('customError-') > -1) {
                customErrors.push(sessionStorage[key]);
            }
        }
        if (personalInfo && !personalInfo.governmentId && customErrors
            && customErrors.length === 1 && sessionStorage.key(0) === 'validationStatus-governmentId') {
            sessionStorage.removeItem('validationStatus-governmentId');
            customErrors = [];
        }
        return customErrors;
    }

    /**
     * @description this method will emit the perosnal information
     * @date 2018-08-15
     * @param {PersonalInformation} personalInfo
     * @memberof PersonalInfoFormComponent
     */
    submitPersonalInfo(personalInfo: PersonalInformation, personalInfoFormValidated: boolean) {
        this.personalInfoFormValidated = personalInfoFormValidated;
        if (personalInfo) {
            this.personalInformation = this._commonService.trimObjectValues(personalInfo);
        } else {
            this.hideErrorElement(false);
        }
       this.personalInfoSubmit.emit(this.personalInformation);
    }

    /**
     * @description this metho will used to translate the data
     * for schema form fields
     * @date 2018-08-15
     * @param {*} jsonObject
     * @memberof PersonalInfoFormComponent
     */
    getTranslatedSchemaData(jsonObject: any): void {
        // Translate Schema form fields
        if (jsonObject && jsonObject.schema && jsonObject.schema.properties) {
            const jsonSchemaKeys = Object.keys(jsonObject.schema.properties);
            if (jsonSchemaKeys) {
                jsonSchemaKeys.forEach((obj: any) => {
                    // Translate Schema Form fields..
                    let translatedText = jsonObject.schema.properties[obj].placeholder || jsonObject.schema.properties[obj].selectText;
                    if (translatedText.endsWith('*')) {
                        translatedText = translatedText.slice(0, -1);
                    }
                    let translatedValue = this._translatePipe.get('schemaform.' + translatedText)['value'];
                    translatedValue = translatedValue ? translatedValue : translatedText;
                    if (jsonObject.schema.required) {
                        if (jsonObject.schema.required.indexOf(obj) > -1) {
                            translatedValue += '*';
                        }
                    }
                    const translateKeyValue = this._translateKey.transform(translatedValue, false);
                    translatedValue = translateKeyValue ? translateKeyValue : translatedValue;
                    if (jsonObject.schema.properties[obj].placeholder) {
                        jsonObject.schema.properties[obj].placeholder = translatedValue;
                    } else if (jsonObject.schema.properties[obj].selectText) {
                        jsonObject.schema.properties[obj].selectText = translatedValue;
                    }
                    // Unique Validation Message Translation
                    if (jsonObject.schema.properties[obj].uniqueValidationMessage) {
                        const errorTranslatedText = jsonObject.schema.properties[obj].uniqueValidationMessage;
                        let errorTranslatedValue = this._translatePipe.get('schemaform.' + errorTranslatedText)['value'];
                        errorTranslatedValue = errorTranslatedValue ? errorTranslatedValue : errorTranslatedText;
                        const errorTranslateKeyValue = this._translateKey.transform(errorTranslatedValue, false);
                        errorTranslatedValue = errorTranslateKeyValue ? errorTranslateKeyValue : errorTranslatedValue;
                        jsonObject.schema.properties[obj].uniqueValidationMessage = errorTranslatedValue;
                    }
                    // Translate Validation messages..
                    if (jsonObject.schema.properties[obj].validationMessages) {
                        const validationMessagesKeys = Object.keys(jsonObject.schema.properties[obj].validationMessages);
                        validationMessagesKeys.forEach((validationObj) => {
                            if (validationObj) {
                                const errorTranslatedText = jsonObject.schema.properties[obj].validationMessages[validationObj];
                                if (errorTranslatedText) {
                                    let errorTranslatedValue = this._translatePipe.get('schemaform.' + errorTranslatedText)['value'];
                                    errorTranslatedValue = errorTranslatedValue ? errorTranslatedValue : errorTranslatedText;
                                    const errorTranslateKeyValue = this._translateKey.transform(errorTranslatedValue, false);
                                    errorTranslatedValue = errorTranslateKeyValue ? errorTranslateKeyValue : errorTranslatedValue;
                                    jsonObject.schema.properties[obj].validationMessages[validationObj] = errorTranslatedValue;
                                }
                            }
                        });
                    }
                });
            }
        }
        // Translate Schema form Button Text..
        if (jsonObject && (jsonObject.form || jsonObject.layout)) {
            const jsonObjectForm = jsonObject.form || jsonObject.layout;
            for (let i = 0; i < jsonObjectForm.length; i++) {
                jsonObjectForm[i].items = this.searchButtonTitle(jsonObjectForm[i].items);
            }
        }
        this.displayPersonalInfoForm = true;
    }
    /** Search Button text
     * @param  {any} items
     */
    searchButtonTitle(items: any) {
        if (items) {
            items.forEach(element => {
                if (element.title) {
                    const translatedText = element.title;
                    let translatedValue = this._translatePipe.get('schemaform.' + translatedText)['value'];
                    translatedValue = this._translateKey.transform(translatedValue, false);
                    element.title = translatedValue;
                }
                if (element.items && element.items instanceof Array) {
                    const nestedItems = element.items;
                    this.searchButtonTitle(nestedItems);
                }
            });
        }
        return items;
    }

    /**
      */
    ngOnDestroy(): void {
        this._chRef.detach();
        if (this.editPersonalSubscription) {
            this.editPersonalSubscription.unsubscribe();
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/customerservice/components/enrollment/personal-schema-info.component.ts